'use client'

/**
 * ToolsRow — Bookmark / Like / Share as a 3-button tile row with a share
 * popover (X / Facebook / Pinterest / Reddit / Copy link). Shared by the
 * tutorial and scene detail pages so both render identical engagement tools.
 * `contentId` is resolved server-side so the first toggle doesn't wait on a
 * lookup. (Extracted from tutorial-view.tsx.)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Bookmark, Heart, Share2, Copy } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { createClient } from '@/lib/supabase/client'
import { localizeSiteUrl } from '@/lib/i18n/share-url'

export function ToolsRow({
  contentId,
  userId,
  shareUrl: canonicalShareUrl,
  shareTitle,
}: {
  contentId: string | null
  userId: string | null
  shareUrl: string
  shareTitle: string
}) {
  const supabase = createClient()
  // Callers pass the canonical (EN, un-prefixed) URL; share the page in the
  // locale it is being read in (/ko/tutorials/<slug>), English stays bare.
  const shareUrl = localizeSiteUrl(canonicalShareUrl, useLocale())
  const t = useTranslations('engagement')
  const tShare = useTranslations('share')
  // Locale-aware router: signed-out like/bookmark sends /ko readers to
  // /ko/auth, not the English /auth.
  const router = useRouter()

  const [liked, setLiked] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [likeCount, setLikeCount] = useState<number | null>(null)
  // Bumped on each toggle → used as the icon `key` so the pop animation
  // replays on both select and deselect.
  const [likePulse, setLikePulse] = useState(0)
  const [bookmarkPulse, setBookmarkPulse] = useState(0)

  // Hydrate the user's like/bookmark state + the public like count.
  // Runs once on mount (and whenever the contentId / userId changes).
  const fetchState = useCallback(async () => {
    if (!contentId) return
    const { count } = await supabase
      .from('likes')
      .select('*', { count: 'exact', head: true })
      .eq('content_id', contentId)
      .eq('status', 'like')
    setLikeCount(count ?? 0)

    if (!userId) {
      setLiked(false)
      setBookmarked(false)
      return
    }
    const [{ data: l }, { data: b }] = await Promise.all([
      supabase
        .from('likes')
        .select('user_id')
        .eq('user_id', userId)
        .eq('content_id', contentId)
        .eq('status', 'like')
        .maybeSingle(),
      supabase
        .from('bookmarks')
        .select('user_id')
        .eq('user_id', userId)
        .eq('content_id', contentId)
        .eq('status', 'active')
        .maybeSingle(),
    ])
    setLiked(!!l)
    setBookmarked(!!b)
  }, [supabase, contentId, userId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch of like/bookmark state from Supabase
    void fetchState()
  }, [fetchState])

  const handleLike = async () => {
    if (!userId) {
      router.push('/auth')
      return
    }
    if (!contentId) return
    setLikePulse((p) => p + 1)
    const next = !liked
    setLiked(next)
    setLikeCount((c) => (c == null ? c : c + (next ? 1 : -1)))
    await fetch('/api/engagement/like', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentId, active: next }),
    })
  }

  const handleBookmark = async () => {
    if (!userId) {
      router.push('/auth')
      return
    }
    if (!contentId) return
    setBookmarkPulse((p) => p + 1)
    const next = !bookmarked
    setBookmarked(next)
    await fetch('/api/engagement/bookmark', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentId, active: next }),
    })
  }

  // ─── Share popover ──────────────────────────────────────────
  // Mirrors the platforms the old /tutorials/[slug] detail page
  // exposed via the legacy <SocialShare /> component: X, Facebook,
  // Pinterest, Reddit, plus a Copy Link affordance. The 3-tile Tools
  // row keeps a single "Share" tile (so the row layout stays); tap
  // it to reveal the platform sheet right above the row.
  const [shareOpen, setShareOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const shareRef = useRef<HTMLDivElement>(null)

  // Click-outside / Escape closes the popover.
  useEffect(() => {
    if (!shareOpen) return
    const onClick = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShareOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShareOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [shareOpen])

  const encodedUrl = encodeURIComponent(shareUrl)
  const encodedTitle = encodeURIComponent(shareTitle)
  const sharePlatforms: { name: string; href: string; svg: React.ReactNode }[] = [
    {
      name: 'X',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      svg: (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      svg: (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    {
      name: 'Pinterest',
      href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`,
      svg: (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
          <path d="M12 0a12 12 0 0 0-4.373 23.17c-.07-.937-.134-2.382.028-3.408.146-.93.946-5.917.946-5.917s-.242-.484-.242-1.2c0-1.123.652-1.963 1.462-1.963.69 0 1.022.518 1.022 1.138 0 .694-.442 1.73-.669 2.691-.19.803.403 1.459 1.195 1.459 1.433 0 2.534-1.511 2.534-3.691 0-1.93-1.387-3.28-3.369-3.28-2.294 0-3.641 1.72-3.641 3.5 0 .693.267 1.435.6 1.838a.24.24 0 0 1 .056.231c-.061.255-.198.803-.225.915-.035.149-.116.181-.268.109-.998-.465-1.622-1.925-1.622-3.098 0-2.523 1.834-4.84 5.286-4.84 2.775 0 4.932 1.977 4.932 4.62 0 2.757-1.739 4.976-4.151 4.976-.811 0-1.573-.421-1.834-.919l-.498 1.902c-.181.696-.669 1.567-.995 2.098A12 12 0 1 0 12 0z" />
        </svg>
      ),
    },
    {
      name: 'Reddit',
      href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      svg: (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.11 3.11 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
      ),
    },
  ]

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch {
      // Older Safari fallback
      const input = document.createElement('input')
      input.value = shareUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div ref={shareRef} className="relative">
      <div className="grid grid-cols-3 gap-1.5">
        <ToolButton
          icon={Bookmark}
          label={t('bookmark')}
          active={bookmarked}
          accent="bookmark"
          pulse={bookmarkPulse}
          onClick={handleBookmark}
        />
        <ToolButton
          icon={Heart}
          label={
            likeCount != null && likeCount > 0
              ? t('likeWithCount', { count: likeCount })
              : t('like')
          }
          active={liked}
          accent="like"
          pulse={likePulse}
          onClick={handleLike}
        />
        <ToolButton
          icon={Share2}
          label={t('share')}
          active={shareOpen}
          onClick={() => setShareOpen((v) => !v)}
        />
      </div>

      {shareOpen && (
        <div
          role="dialog"
          aria-label={t('share')}
          className="absolute right-0 top-full z-30 mt-2 w-60 border border-border bg-card p-3 shadow-lg"
        >
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('share')}
          </div>
          <div className="flex items-center gap-1">
            {sharePlatforms.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault()
                  window.open(p.href, '_blank', 'width=600,height=480')
                }}
                aria-label={tShare('shareOn', { network: p.name })}
                title={tShare('shareOn', { network: p.name })}
                className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                style={{ background: 'color-mix(in srgb, var(--rope) 8%, var(--background))' }}
              >
                {p.svg}
              </a>
            ))}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="mt-2 flex w-full items-center justify-center gap-2 border border-border bg-card px-3 py-2 text-[12px] font-medium text-foreground transition-colors hover:border-rope"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            {copied ? t('linkCopied') : t('copyLink')}
          </button>
        </div>
      )}
    </div>
  )
}

function ToolButton({
  icon: Icon,
  label,
  active,
  accent,
  pulse = 0,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  active?: boolean
  /** Toggle accent — colorizes the icon when active (red heart / blue bookmark). */
  accent?: 'like' | 'bookmark'
  /** Bumped by the parent on each toggle to replay the pop animation. */
  pulse?: number
  onClick?: () => void
}) {
  // Like/bookmark colorize the ICON (no tile highlight); Share (no accent)
  // keeps a neutral "open" tile state. The old orange rope box is gone.
  const iconActive =
    active && accent
      ? accent === 'like'
        ? 'fill-red-500 text-red-500'
        : 'fill-brand-700 text-brand-700'
      : ''
  const tileActive = active && !accent
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 border py-2.5 text-[11px] font-medium text-foreground transition-colors ${
        tileActive
          ? 'border-foreground/25 bg-muted'
          : 'border-border bg-card hover:border-rope hover:bg-rope/5 focus-visible:border-rope focus-visible:outline-none'
      }`}
    >
      <Icon
        key={pulse}
        className={`h-4 w-4 transition-colors ${iconActive} ${pulse > 0 ? 'animate-icon-pop' : ''}`}
      />
      {label}
    </button>
  )
}
